import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setProductData, updateProduct, addProductType, toggleTypeLink, updateLinkWinnerConfig } from '../store/productSlice';
import { PW_Products_API, PW_Product_Save_API, PW_ProductTypeLink_Toggle_API, PW_WinnerConfig_Save_API, PW_ProductType_Save_API } from '../Server/ApiV2';
import Spinner from './Spinner';
import Pick6Options from './Pick6Options';
import { PICK6, PICK6_LABEL, PICK6_MAX_WINNERS, isPick6, winnerProblems } from '../utils/pick6';
// denominations are derived per product from linked types' allowedDenominations

export default function Products() {
  const dispatch = useDispatch();
  const { products, productTypes, productTypeLinks } = useSelector(s => s.product);
  const [pageLoading, setPageLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await PW_Products_API();
      if (cancelled) return;
      if (res?.data?.success) dispatch(setProductData(res.data.data));
      setPageLoading(false);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);
  const [editingLink, setEditingLink] = useState(null);
  const [editWinnerConfig, setEditWinnerConfig] = useState({});
  const [addingType, setAddingType] = useState(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeLabel, setNewTypeLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null); // { id, text }

  const MATCHING_METHODS = [
    { value: 'any3', label: 'Any 3 Identical Numbers' },
    { value: 'line3', label: '3 Numbers Consecutive - Row, Column & Any Diagonal' },
    { value: 'any2', label: 'Any 2 Identical Numbers (Min 20, 1/2/3 combo)' },
  ];

  const handleSave = async (p) => {
    // A Pick 6 product keeps its method and 100 coupons per batch (the server
    // refuses to change either), so those fields aren't on its form.
    const p6 = isPick6(p);
    const method = p6 ? PICK6 : document.getElementById(`method-${p.id}`).value;
    const updateData = {
      id: p.id, name: p.name,
      rsp: Number(document.getElementById(`rsp-${p.id}`).value),
      priceAmount: Number(document.getElementById(`price-${p.id}`).value) || 1,
      matchingMethod: method,
      winCondition: method,
      winDescription: p6 ? PICK6_LABEL : MATCHING_METHODS.find(m => m.value === method)?.label || method,
      cardLayout: p.cardLayout, cardsPerBundle: p6 ? 100 : Number(document.getElementById(`bundle-${p.id}`).value) || p.cardsPerBundle, bundlesPerSet: p.bundlesPerSet,
      resetDaily: document.getElementById(`reset-${p.id}`).checked,
      status: document.getElementById(`status-${p.id}`).value,
    };
    if (!p6) {
      dispatch(updateProduct(updateData));
      PW_Product_Save_API(updateData).catch(err => console.error('[Products] Save failed:', err));
      setEditing(null);
      return;
    }
    if (updateData.status === 'Active' && !(updateData.rsp > 0)) {
      setSaveError({ id: p.id, text: 'Set the RSP above 0 before making this product Active.' });
      return;
    }
    setSaving(true);
    setSaveError(null);
    const res = await PW_Product_Save_API(updateData);
    setSaving(false);
    if (!res?.data?.success) {
      setSaveError({
        id: p.id,
        text: res?.response?.data?.message
          ? `${res.response.data.message} Nothing was changed.`
          : 'Could not reach the server. Nothing was changed.',
      });
      return;
    }
    dispatch(updateProduct(updateData));
    setEditing(null);
  };

  const handleEditLink = (link) => {
    const key = `${link.productId}-${link.typeId}`;
    if (editingLink === key) {
      setEditingLink(null);
      return;
    }
    setEditingLink(key);
    const type = productTypes.find(t => t.id === link.typeId);
    const pDenoms = [...(type?.allowedDenominations || [])].sort((a, b) => a - b);
    const config = {};
    pDenoms.forEach(d => { config[d] = link.winnerConfig?.[d] || 0; });
    setEditWinnerConfig(config);
  };

  const handleLinkConfigChange = (denom, value, max = 99) => {
    const v = Math.max(0, Math.min(max, parseInt(value) || 0));
    setEditWinnerConfig(prev => ({ ...prev, [denom]: v }));
  };

  const handleSaveLink = (productId, typeId) => {
    const config = { ...editWinnerConfig };
    dispatch(updateLinkWinnerConfig({ productId, typeId, winnerConfig: config }));
    PW_WinnerConfig_Save_API({ productId, typeId, winnerConfig: config }).catch(err => console.error('[Products] WinnerConfig save failed:', err));
    setEditingLink(null);
  };

  const getTotals = (config) => {
    const totalWinners = Object.values(config || {}).reduce((s, v) => s + (v || 0), 0);
    const totalPrize = Object.entries(config || {}).reduce((s, [d, c]) => s + Number(d) * (c || 0), 0);
    return { totalWinners, totalPrize };
  };

  const handleAddType = (productId) => {
    const name = newTypeName.trim().toUpperCase();
    const label = newTypeLabel.trim();
    if (!name) return;
    if (productTypes.some(t => t.id === name || t.name === name)) {
      const existing = productTypes.find(t => t.id === name || t.name === name);
      const alreadyLinked = productTypeLinks.find(l => l.productId === productId && l.typeId === existing.id && l.linked);
      if (!alreadyLinked) {
        dispatch(toggleTypeLink({ productId, typeId: existing.id }));
        PW_ProductTypeLink_Toggle_API({ productId, typeId: existing.id }).catch(err => console.error('[Products] Link toggle failed:', err));
      }
      setAddingType(null);
      setNewTypeName('');
      setNewTypeLabel('');
      return;
    }
    const existingLinks = productTypeLinks.filter(l => l.productId === productId && l.linked);
    const denoms = [...new Set(existingLinks.flatMap(l => { const t = productTypes.find(tp => tp.id === l.typeId); return t?.allowedDenominations || []; }))].sort((a, b) => a - b);
    const typeData = { id: name, name: name, label: label || name, allowedDenominations: denoms, status: 'Active' };
    dispatch(addProductType(typeData));
    PW_ProductType_Save_API(typeData).catch(err => console.error('[Products] Type save failed:', err));
    dispatch(toggleTypeLink({ productId, typeId: name }));
    PW_ProductTypeLink_Toggle_API({ productId, typeId: name }).catch(err => console.error('[Products] Link toggle failed:', err));
    setAddingType(null);
    setNewTypeName('');
    setNewTypeLabel('');
  };

  if (pageLoading) return <div className="page-loading"><Spinner /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Products</h2>
          <div className="page-header-sub">Configure products, link types, and set winners</div>
        </div>
      </div>

      {products.map(p => {
        const isEditing = editing === p.id;
        const links = productTypeLinks.filter(l => l.productId === p.id);
        const linkedCount = links.filter(l => l.linked).length;
        const linkedOnly = links.filter(l => l.linked);
        const prodDenoms = [...new Set(links.flatMap(l => { const t = productTypes.find(tp => tp.id === l.typeId); return t?.allowedDenominations || []; }))].sort((a, b) => a - b);
        const footerDenoms = {};
        prodDenoms.forEach(d => {
          footerDenoms[d] = linkedOnly.reduce((sum, l) => sum + (l.winnerConfig?.[d] || 0), 0);
        });
        const footerWinners = Object.values(footerDenoms).reduce((s, v) => s + v, 0);
        const factor = p.priceAmount || 1;
        const footerPrize = Object.entries(footerDenoms).reduce((s, [d, c]) => s + Number(d) * c, 0) * factor;
        const p6 = isPick6(p);

        return (
          <div key={p.id} className="card mb-24">
            <div className={`card-title ${p.status === 'Active' ? 'green' : ''}`}>
              {p.name}
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <span className={`chip ${p.status === 'Active' ? 'chip-green' : 'chip-red'}`}>{p.status}</span>
              </span>
            </div>
            <div className="card-body">
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div>
                  <div className="text-sm text-muted mb-8">RSP</div>
                  {isEditing ? (
                    <input type="number" className="form-input" defaultValue={p.rsp} id={`rsp-${p.id}`} style={{ width: 80 }} />
                  ) : (
                    <div className="fw-700" style={{ fontSize: 24, color: '#c9a227' }}>{p.rsp}</div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-muted mb-8">Factor</div>
                  {isEditing ? (
                    <input type="number" className="form-input" defaultValue={p.priceAmount || 1} id={`price-${p.id}`} style={{ width: 80 }} />
                  ) : (
                    <div className="fw-700" style={{ fontSize: 24, color: '#2d1b69' }}>{p.priceAmount || 1}</div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-muted mb-8">Matching Method</div>
                  {isEditing && !p6 ? (
                    <select className="form-select" defaultValue={p.matchingMethod || p.winCondition} id={`method-${p.id}`} style={{ width: '100%' }}>
                      {MATCHING_METHODS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="fw-600">{p.winDescription}</div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-muted mb-8">Card Layout</div>
                  {p6 ? (
                    <div className="fw-600">6 of 40 boxes (100 coupons per batch)</div>
                  ) : isEditing ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="fw-600">{p.cardLayout}</span>
                      <input type="number" className="form-input" defaultValue={p.cardsPerBundle} id={`bundle-${p.id}`} style={{ width: 80 }} min={10} max={500} />
                      <span className="text-sm text-muted">cards</span>
                    </div>
                  ) : (
                    <div className="fw-600">{p.cardLayout} ({p.cardsPerBundle} cards/bundle)</div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-muted mb-8">Status</div>
                  {isEditing ? (
                    <select className="form-select" defaultValue={p.status} id={`status-${p.id}`} style={{ width: 120 }}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  ) : (
                    <div className="fw-600">{p.bundlesPerSet} bundles/set</div>
                  )}
                </div>
                <div>
                  <div className="text-sm text-muted mb-8">Coupon Bundle</div>
                  {isEditing ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" defaultChecked={p.resetDaily !== false} id={`reset-${p.id}`} />
                      <span className="text-sm fw-600">Reset daily<br /><span className="text-sm text-muted" style={{ fontWeight: 400 }}>(uncheck = carry balance to next day)</span></span>
                    </label>
                  ) : (
                    <div className="fw-600">{p.resetDaily !== false ? 'Resets daily' : 'Carries over'}</div>
                  )}
                </div>
              </div>

              {prodDenoms.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div className="text-sm text-muted mb-8">Denominations</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {prodDenoms.map(d => (
                      <span key={d} className="chip chip-gold" style={{ fontSize: 13, fontWeight: 700 }}>
                        {d >= 1000 ? `${(d / 1000).toLocaleString()}K` : d.toLocaleString()}
                      </span>
                    ))}
                    <span className="text-sm text-muted" style={{ alignSelf: 'center', marginLeft: 4 }}>
                      ({prodDenoms.length} values)
                    </span>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {isEditing ? (
                  <>
                    <button className="btn btn-success btn-sm" disabled={saving} onClick={() => handleSave(p)}>{saving ? 'Saving…' : 'Save'}</button>
                    <button className="btn btn-secondary btn-sm" disabled={saving} onClick={() => { setEditing(null); setSaveError(null); }}>Cancel</button>
                  </>
                ) : (
                  <button className="btn btn-outline btn-sm" onClick={() => { setEditing(p.id); setSaveError(null); }}>Edit Product</button>
                )}
              </div>
              {saveError?.id === p.id && <div className="login-error">{saveError.text}</div>}

              <div>
                <div className="text-sm fw-600 mb-8" style={{ color: '#2d1b69', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span>Linked Types ({linkedCount})</span>
                  {addingType !== p.id ? (
                    <button className="btn btn-sm" style={{ padding: '2px 10px', fontSize: 11, background: '#2d1b69', color: '#fff', border: 'none', borderRadius: 4 }}
                      onClick={() => { setAddingType(p.id); setNewTypeName(''); setNewTypeLabel(''); }}>+ Add Type</button>
                  ) : (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="text" placeholder="ID (e.g. N4)" value={newTypeName}
                        onChange={e => setNewTypeName(e.target.value)}
                        style={{ width: 70, padding: '3px 6px', fontSize: 12, border: '1.5px solid #e0dce8', borderRadius: 4 }} />
                      <input type="text" placeholder="Label (e.g. Normal 4)" value={newTypeLabel}
                        onChange={e => setNewTypeLabel(e.target.value)}
                        style={{ width: 120, padding: '3px 6px', fontSize: 12, border: '1.5px solid #e0dce8', borderRadius: 4 }} />
                      <button className="btn btn-success btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                        disabled={!newTypeName.trim()}
                        onClick={() => handleAddType(p.id)}>Add</button>
                      <button className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                        onClick={() => setAddingType(null)}>Cancel</button>
                    </div>
                  )}
                </div>
                <div className="table-wrap">
                  <table className="winner-config-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', position: 'sticky', left: 0, background: '#f5f3fa', zIndex: 1 }}>Type</th>
                        {prodDenoms.map(d => (
                          <th key={d}>{d >= 1000 ? `${d / 1000}K` : d}</th>
                        ))}
                        <th style={{ background: '#ede8f5' }}>Total</th>
                        <th style={{ background: '#fef9e7' }}>Prize</th>
                        <th style={{ background: '#e8f5e9' }}>Every X</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {productTypes.filter(type => {
                        const lnk = links.find(l => l.typeId === type.id);
                        return !!lnk;
                      }).sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })).map(type => {
                        const link = links.find(l => l.typeId === type.id);
                        const isLinked = link?.linked ?? false;
                        const config = link?.winnerConfig || {};
                        const { totalWinners, totalPrize } = getTotals(config);
                        const linkKey = `${p.id}-${type.id}`;
                        const isEditingThis = editingLink === linkKey;
                        // Pick 6: each winner is one winning coupon, so up to 100 per 100 coupons,
                        // and every prize with winners must be printable on its options.
                        const editTotal = isEditingThis ? getTotals(editWinnerConfig).totalWinners : 0;
                        const overLimit = isEditingThis && (p6
                          ? editTotal > PICK6_MAX_WINNERS
                          : p.matchingMethod !== 'any2' && editTotal >= p.cardsPerBundle);
                        const editProblems = isEditingThis && p6
                          ? winnerProblems([{ linked: true, typeId: type.id, winnerConfig: editWinnerConfig }], p.winningOptions)
                          : [];

                        return (
                          <React.Fragment key={type.id}>
                            <tr style={{ opacity: isLinked ? 1 : 0.45, background: isEditingThis ? '#f9f8fc' : 'transparent' }}>
                              <td style={{ textAlign: 'left', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: isEditingThis ? '#f9f8fc' : '#fff', zIndex: 1 }}>
                                <label className="toggle-switch" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                                  <input type="checkbox" checked={isLinked}
                                    onChange={() => {
                                      dispatch(toggleTypeLink({ productId: p.id, typeId: type.id }));
                                      PW_ProductTypeLink_Toggle_API({ productId: p.id, typeId: type.id }).catch(err => console.error('[Products] Link toggle failed:', err));
                                    }} />
                                  <span className="toggle-slider"></span>
                                </label>
                                <span className="fw-700">{type.name}</span>
                              </td>
                              {prodDenoms.map(d => (
                                <td key={d}>
                                  {isEditingThis ? (
                                    <input type="number" min={0} max={p6 ? PICK6_MAX_WINNERS : 99}
                                      value={editWinnerConfig[d] || 0}
                                      onChange={e => handleLinkConfigChange(d, e.target.value, p6 ? PICK6_MAX_WINNERS : 99)}
                                      style={{ width: '100%', maxWidth: 38, padding: '4px 1px', textAlign: 'center',
                                        border: '1.5px solid #e0dce8', borderRadius: 5,
                                        fontSize: 12, fontWeight: 600, boxSizing: 'border-box' }} />
                                  ) : (
                                    <span className={`fw-600 ${(config[d] || 0) > 0 ? '' : 'text-muted'}`}>
                                      {config[d] || 0}
                                    </span>
                                  )}
                                </td>
                              ))}
                              <td className="fw-700" style={{ color: '#2d1b69' }}>{isLinked ? totalWinners : '—'}</td>
                              <td className="fw-700" style={{ color: '#c9a227' }}>{isLinked ? (totalPrize * factor).toLocaleString() : '—'}</td>
                              <td className="fw-700" style={{ color: '#00895e' }}>
                                {isLinked && totalWinners > 0 ? Math.round(p.cardsPerBundle / totalWinners) : '—'}
                              </td>
                              <td>
                                {isLinked && (
                                  isEditingThis ? (
                                    <div style={{ display: 'flex', gap: 4 }}>
                                      <button className="btn btn-success btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                                        disabled={overLimit || editProblems.length > 0}
                                        onClick={() => handleSaveLink(p.id, type.id)}>Save</button>
                                      <button className="btn btn-secondary btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                                        onClick={() => setEditingLink(null)}>X</button>
                                    </div>
                                  ) : (
                                    <button className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }}
                                      onClick={() => handleEditLink(link)}>Edit</button>
                                  )
                                )}
                              </td>
                            </tr>
                            {overLimit && (
                              <tr><td colSpan={prodDenoms.length + 5}>
                                <div className="login-error" style={{ fontSize: 12, margin: '0 0 4px' }}>
                                  {p6
                                    ? `Total winners can be at most ${PICK6_MAX_WINNERS} per 100 coupons.`
                                    : `Total winners must be less than ${p.cardsPerBundle} cards per bundle.`}
                                </div>
                              </td></tr>
                            )}
                            {editProblems.map(u => (
                              <tr key={`${u.kind}-${u.prize}`}><td colSpan={prodDenoms.length + 5}>
                                <div className="login-error" style={{ fontSize: 12, margin: '0 0 4px' }}>
                                  {`No winning option has prize ${u.prize.toLocaleString()}. Add it under Winning Options first.`}
                                </div>
                              </td></tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    {linkedCount > 0 && (
                      <tfoot>
                        <tr className="footer-total">
                          <td style={{ textAlign: 'left', position: 'sticky', left: 0, background: '#ede8f5', zIndex: 1 }}>
                            <span className="fw-700">Total</span>
                          </td>
                          {prodDenoms.map(d => (
                            <td key={d} className="fw-700">{footerDenoms[d] || 0}</td>
                          ))}
                          <td className="fw-700" style={{ color: '#2d1b69' }}>{footerWinners}</td>
                          <td className="fw-700" style={{ color: '#c9a227' }}>{footerPrize.toLocaleString()}</td>
                          <td className="fw-700" style={{ color: '#00895e' }}>
                            {footerWinners > 0 && linkedCount > 0 ? Math.round((p.cardsPerBundle * linkedCount) / footerWinners) : '—'}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {p6 && <Pick6Options product={p} denominations={prodDenoms} links={links} />}

              {linkedCount > 0 && (() => {
                const isAny2 = (p.matchingMethod || p.winCondition) === 'any2';
                const minPrize = isAny2 ? 20 : 0;
                const scale = 100 / p.cardsPerBundle;
                const avgConfigPrize = Math.round(footerPrize / linkedCount * scale);
                const avgTypeWinners = Math.round(footerWinners / linkedCount * scale);
                const avgGuaranteed = (100 - avgTypeWinners) * minPrize * factor;
                const avgTotalPrize = avgConfigPrize + avgGuaranteed;
                const revenue = p.rsp * 100;
                const avgPnL = revenue - avgTotalPrize;
                const totalRevenue = revenue * linkedCount;
                const totalGuaranteed = linkedOnly.reduce((s, l) => {
                  const tw = Object.values(l.winnerConfig || {}).reduce((a, v) => a + (v || 0), 0);
                  return s + Math.round((100 - tw * scale) * minPrize * factor);
                }, 0);
                const totalConfigPrize = Math.round(footerPrize * scale);
                const totalPrize = totalConfigPrize + totalGuaranteed;
                const totalPnL = totalRevenue - totalPrize;
                return (
                <div style={{ marginTop: 16 }}>
                  <div className="text-sm fw-600 mb-8" style={{ color: '#2d1b69' }}>
                    Economics per 100 Coupons
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                    <div style={{ background: '#f0f7ff', borderRadius: 8, padding: '12px 16px', border: '1px solid #d0e3ff' }}>
                      <div className="text-sm text-muted">Selling Value</div>
                      <div className="fw-700" style={{ fontSize: 20, color: '#2d1b69' }}>
                        {revenue.toLocaleString()}
                      </div>
                      <div className="text-sm text-muted">RSP {p.rsp} × 100 coupons</div>
                    </div>
                    <div style={{ background: '#fff8e6', borderRadius: 8, padding: '12px 16px', border: '1px solid #ffe0a0' }}>
                      <div className="text-sm text-muted">Avg Prize Payout</div>
                      <div className="fw-700" style={{ fontSize: 20, color: '#c9a227' }}>
                        {avgTotalPrize.toLocaleString()}
                      </div>
                      <div className="text-sm text-muted">
                        {isAny2
                          ? `Config ${avgConfigPrize.toLocaleString()} + Min ${avgGuaranteed.toLocaleString()}`
                          : `Across ${linkedCount} type${linkedCount > 1 ? 's' : ''}`}
                      </div>
                    </div>
                    <div style={{ background: avgPnL >= 0 ? '#e8f5e9' : '#fde8e8', borderRadius: 8, padding: '12px 16px', border: `1px solid ${avgPnL >= 0 ? '#a5d6a7' : '#f5c6c6'}` }}>
                      <div className="text-sm text-muted">Expected P&L</div>
                      <div className="fw-700" style={{ fontSize: 20, color: avgPnL >= 0 ? '#00895e' : '#e74c3c' }}>
                        {avgPnL.toLocaleString()}
                      </div>
                      <div className="text-sm text-muted">Selling − Prize</div>
                    </div>
                  </div>
                  {linkedCount > 1 && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 8 }}>
                      <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '12px 16px', border: '1px solid #e0dce8' }}>
                        <div className="text-sm text-muted">Total Selling ({linkedCount} types × 100)</div>
                        <div className="fw-700" style={{ fontSize: 20, color: '#2d1b69' }}>
                          {totalRevenue.toLocaleString()}
                        </div>
                      </div>
                      <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '12px 16px', border: '1px solid #e0dce8' }}>
                        <div className="text-sm text-muted">Total Prize ({linkedCount} types × 100)</div>
                        <div className="fw-700" style={{ fontSize: 20, color: '#c9a227' }}>
                          {totalPrize.toLocaleString()}
                        </div>
                      </div>
                      <div style={{ background: '#f5f3fa', borderRadius: 8, padding: '12px 16px', border: '1px solid #e0dce8' }}>
                        <div className="text-sm text-muted">Total P&L ({linkedCount} types × 100)</div>
                        <div className="fw-700" style={{ fontSize: 20, color: totalPnL >= 0 ? '#00895e' : '#e74c3c' }}>
                          {totalPnL.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
}
